// @ts-nocheck
import { isChrome } from "./Browser";
import { interactivityRegistration } from "./Charta";
import { DomQuery, HtmlClassSwitch, HtmlClasses, WithClass, WithTagName } from "./Dom";
import { Enumeration } from "./Enumeration";
import { AvailableSizeChangedEvent, VisibilityChangedEvent } from "./Event";
import { Tristate } from "./Tristate";
import { WebPageComponent, connectClickOutsideListener, removeClickOutsideListener } from "./WebSite.Page.Component";

const StyleMode = new Enumeration(["Initial", "Hover", "Select"]);
const MapMode = new Enumeration(["Normal", "Light", "Dark"]);

export class MapComponent extends WebPageComponent {
  constructor(element) {
    super(element);

    this.zoomLevel = parseFloat(element.dataset.ZoomLevel);
    this.opacity = parseFloat(element.dataset.Opacity);
    this.interactive = element.dataset.Interactive === "true";
    this.mode = MapMode.fromText(element.dataset.Mode);
    this.locked = false;
    this.lockedClass = new HtmlClassSwitch(this.element, "Locked");
    this.legendEnabled = new HtmlClassSwitch(this.element, "LegendEnabled");

    this.map = null;
    this.resizeObserver = null;

    this.layers = null;
    this.inspector = null;

    this.determineElements();
    this.createMap();

    this.map.render();
    this.attachHandlers();
    this.refresh();
  }

  attachHandlers() {
    this.layerButton.addEventListener(
      "click",
      (event) => {
        if (!this.locked)
          this.toggleComponent(this.layers, event);
      }
    );

    if (this.legendButton !== null)
      this.legendButton.addEventListener(
        "click",
        (event) => {
          if (!this.locked)
            this.toggleComponent(this.legend, event);
        }
      );

    if (this.configurationButton !== null)
      this.configurationButton.addEventListener(
        "click",
        (event) => {
          if (!this.locked)
            this.toggleComponent(this.configuration, event);
        }
      );

    this.stylesheet.addEventListener(
      "change",
      (event) => {
        if (!this.locked)
          this.update(
            () => {
              this.refreshMap(false);
            }
          );
      }
    );

    this.restoreButton.addEventListener(
      "click",
      (event) => {
        if (!this.locked)
          this.restore();
      }
    );

    this.map.on("moveend",
      (event) => {
        if (!this.locked) {
          this.update(
            () => {
              if (this.interactive)
                this.refreshMap(false);
            }
          );
        }
      }
    );
  }

  bind() {
    this.map.updateSize();
  }

  observeResize() {
    this.resizeObserver = new ResizeObserver(
      () => {
        this.map.updateSize();
      }
    );

    this.resizeObserver.observe(this.map.getTargetElement());
  }

  createHoverInteraction() {
    const hover = new ol.interaction.Select({
      condition: ol.events.condition.pointerMove,
      style: WebPageMapComponentHoverStyle
    });

    hover.on("select", event => this.hover(event));
    return hover;
  }

  applyFilter(context) {
    switch (this.mode) {
      case MapMode.Light: {
        context.filter = "grayscale(90%)";
        context.globalCompositeOperation = "source-over";

        break;
      }
      case MapMode.Dark: {
        context.filter = "grayscale(90%) invert(80%) hue-rotate(180deg)";
        context.globalCompositeOperation = "source-over";

        break;
      }
    }
  }

  createMap() {
    const mapLayer = new ol.layer.Tile({
      source: new ol.source.OSM({
        wrapX: false
      })
    });

    mapLayer.on(
      "precompose",
      (event) => {
        if (event.context)
          this.applyFilter(event.context);
      }
    );

    mapLayer.on(
      "postcompose",
      (event) => {
        if (event.context)
          event.context.filter = "none";
      }
    );

    mapLayer.setOpacity(this.opacity);

    this.map = new ol.Map({
      target: this.element.childNodes[0].id,
      layers: [
        mapLayer
      ],
      view: new ol.View({
        center: ol.proj.fromLonLat([0, 0]),
        zoom: this.zoomLevel
      })
    });

    this.hoverInteraction = this.createHoverInteraction();
    this.selectInteraction = this.createSelectInteraction();

    this.map.addInteraction(this.hoverInteraction);
    this.map.addInteraction(this.selectInteraction);

    this.layerSelectors[0].layer = mapLayer;
    this.layerSelectors[0].onchange = this.createToggleLayerHandler(mapLayer, this.layerSelectors[0].selector);

    this.observeResize();
  }

  createSelectInteraction() {
    const select = new ol.interaction.Select({
      condition: ol.events.condition.select,
      style: WebPageMapComponentSelectStyle
    });
    select.on("select", event => this.select(event));
    return select;
  }

  createToggleLayerHandler(layer, selector) {
    return (event) => {
      this.update(
        () => {
          this.updateLayers();
          this.refreshMap(false);
        }
      );
    };
  }

  determineElements() {
    let query = new DomQuery(this.element);

    this.tooltip = query.getChild(WithClass("Tooltip"));

    const actions = query.getChild(WithClass("Actions"));
    query = new DomQuery(actions);

    this.components = query.getChild(WithClass("Components"));
    this.layerButton = query.getChild(WithClass("Layers"));
    this.legendButton = query.getChild(WithClass("Legend"));
    this.restoreButton = query.getChild(WithClass("Restore"));
    this.configurationButton = query.getChild(WithClass("Configuration"));

    query = new DomQuery(this.components);

    this.legend = query.getChild(WithClass("Legend"));
    this.layers = query.getChild(WithClass("Layers"));
    this.inspector = query.getChild(WithClass("Inspector"));
    this.configuration = query.getChild(WithClass("Configuration"));

    query = new DomQuery(this.configuration);
    this.stylesheet = query.getDescendant(WithTagName("TEXTAREA"));

    query = new DomQuery(this.layers);

    this.layerSelectors = query.getDescendants(WithClass("Tristate"));

    for (let index = 0; index < this.layerSelectors.length; index++) {
      const layerSelector = this.layerSelectors[index];

      layerSelector.selector = new Tristate(layerSelector);
      layerSelector.selector.index = index;
    }
  }

  getBounds() {
    const size = this.map.getSize();
    const view = this.map.getView();

    const extent = view.calculateExtent(size);

    const result = ol.proj.transformExtent(
      extent,
      ol.proj.get("EPSG:3857"),
      ol.proj.get("EPSG:4326")
    );

    return result;
  }

  handleEvent(event) {
    if (event instanceof VisibilityChangedEvent) {
      this.map.updateSize();
      this.updateViewPort();
    }
    else if (event instanceof AvailableSizeChangedEvent) {
      this.map.updateSize();
      this.updateViewPort();
    }
  }

  hasEnabledLayers() {
    let result = false;
    let index = 0;

    while (!result && index < this.layerSelectors.length) {
      result = this.layerSelectors[index].selector.state === "Enabled";
      index++;
    }

    return result;
  }

  createLegendTable(object, labels) {
    const exclude = ["style", "geometry", "orientation", "point-type", "point-source"];
    let table = document.createElement("table");
    let row, cell;

    const associatedValue = object.AssociatedValue;

    if (associatedValue !== undefined && associatedValue.length > 0) {
      row = table.insertRow();

      for (const property in associatedValue[0]) {
        const label = labels[property];

        cell = document.createElement("td");
        cell.innerHTML = label;

        row.appendChild(cell);
      }

      for (const element of associatedValue) {
        row = table.insertRow();

        for (var property in element) {
          cell = document.createElement("td");
          cell.innerHTML = element[property];

          row.appendChild(cell);
        }
      }
    }
    else {
      for (const property in object) {
        const label = labels[property];

        if (!exclude.includes(property) && label !== undefined) {
          row = table.insertRow();

          cell = document.createElement("td");
          cell.innerHTML = label;

          row.appendChild(cell);

          cell = document.createElement("td");
          cell.innerHTML = object[property];

          row.appendChild(cell);
        }
      }
    }

    if (table.rows.length === 0)
      table = null;

    return table;
  }

  hover(event) {
    var map = this;
    var source = event.mapBrowserEvent.originalEvent;
    var legend = this.tooltip;

    if (source.ctrlKey) {
      legend.innerHTML = "";

      if (event.selected.length > 0) {
        var feature = event.selected[0];
        var properties = feature.getProperties();
        var layer = map.hoverInteraction.getLayer(feature);

        var table = this.createLegendTable(properties, layer.labels);

        if (table !== null)
          legend.appendChild(table);
      }

      legend.style.position = "fixed";
      legend.style.left = source.clientX + "px";
      legend.style.top = source.clientY + "px";
    }
    else
      legend.innerHTML = "";
  }

  lock() {
    this.setLocked(true);
  }

  async refreshMap(updateViewPort) {
    this.lock();

    try {
      const response = await fetch(
        this.uri,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            Command: "Refresh",
            Value: {
              ZoomLevel: this.map.getView().getZoom()
            }
          })
        }
      );

      const data = await response.json();
      this.updateContents(data);

      if (updateViewPort)
        this.updateViewPort();
    }
    finally {
      this.unlock();
    }
  }

  refresh() {
    this.refreshMap(true);
  }

  restore() {
    this.lock();
    fetch(
      this.uri,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          Command: "Restore"
        })
      }
    )
      .then(response => response.json())
      .then(settings => {
        this.bounds = settings.Bounds;
        this.zoomLevel = settings.ZoomLevel;

        this.updateViewPort();
      })
      .finally(() => this.unlock());
  }

  select(event) {
    var map = this;
    var legend = this.inspector;
    legend.innerHTML = "";

    if (event.selected.length > 0) {
      var feature = event.selected[0];
      var properties = feature.getProperties();
      var layer = map.hoverInteraction.getLayer(feature);

      var table = this.createLegendTable(properties, layer.labels);

      if (table !== null)
        legend.appendChild(table);
    }
  }

  setLocked(locked) {
    this.locked = locked;
    this.lockedClass.setStatus(locked);
  }

  toggleComponent(component, event) {
    var object = this;
    var classes = new HtmlClasses(component);

    if (classes.contains("expanded"))
      removeClickOutsideListener(component.clickOutsideListener);
    else
      component.clickOutsideListener = connectClickOutsideListener(component, function (event) { object.toggleComponent(component, event); })

    classes.toggle("expanded");
    event.stopPropagation();
  }

  unlock() {
    this.setLocked(false);
  }

  update(callback) {
    var layers = new Array();

    for (var index = 1; index < this.layerSelectors.length; index++)
      layers.push(this.layerSelectors[index].selector.state);

    var bounds = this.getBounds();
    var bounds_ = null;

    if (!ol.extent.isEmpty(bounds)) {
      bounds_ = {
        "Left": bounds[0],
        "Top": bounds[1],
        "Right": bounds[2],
        "Bottom": bounds[3]
      };
    }

    this.client.sendJsonRequest(
      this.uri,
      {
        "Command": "Update",
        "Value": {
          "Layers": layers,
          "Bounds": bounds_,
          "ZoomLevel": this.map.getView().getZoom(),
          "Stylesheet": this.stylesheet.value
        }
      },
      callback
    );
  }

  updateContents(contents) {
    this.contents = contents.Layers;
    this.bounds = contents.Settings.Bounds;
    this.zoomLevel = contents.Settings.ZoomLevel;

    for (let index = 0; index < this.contents.length; index++) {
      const feature = this.contents[index];
      const layerSelector = this.layerSelectors[index + 1];
      let layer = null;

      if (feature !== null) {
        const vectorSource = new ol.source.Vector({
          features: (new ol.format.GeoJSON()).readFeatures(feature, { featureProjection: ol.proj.get("EPSG:3857") })
        });

        if (feature.Class === "Vector") {
          layer = new ol.layer.Vector({
            source: vectorSource,
            style: WebPageMapComponentStyle
          });
        }
        else if (feature.Class === "Heatmap") {
          const gradient = feature.properties.Gradient.substring(1, feature.properties.Gradient.length - 1).split(",");
          const scale = feature.properties.Scale;

          layer = new ol.layer.Heatmap({
            source: vectorSource,
            radius: 10,
            blur: 2,
            gradient: gradient,
            weight: (feature) => {
              return Math.max(0.25, parseFloat(feature.getProperties().style["stroke-width"]) / scale);
            }
          });
        }

        layer.setOpacity(feature.Opacity);
        layer.labels = feature.Labels;

        if (layerSelector.layer !== null)
          this.map.removeLayer(layerSelector.layer);

        if (layer !== null)
          this.map.addLayer(layer);
      }
      else if (layerSelector.layer !== null)
        this.map.removeLayer(layerSelector.layer);

      layerSelector.layer = layer;
      layerSelector.onchange = this.createToggleLayerHandler(layer, layerSelector.selector);
    }

    this.updateLegend(contents.Legend);
    this.updateLayers();
  }

  updateLegend(data) {
    const target = this.legend.childNodes[1].tBodies[0];
    target.innerHTML = "";

    for (const legend of data) {
      const row = document.createElement("tr");
      row.classList.add("LegendItem");

      const description = document.createElement("td");
      description.innerHTML = legend.Description;

      row.appendChild(description);

      const icon = document.createElement("td");
      icon.innerHTML = legend.Icon;

      row.appendChild(icon);
      target.appendChild(row);
    }

    this.legendEnabled.setStatus(data.length > 0);
  }

  updateLayer(layer, selector) {
    if (selector.state === "Enabled")
      selector.setEffect(true);
    else if (selector.state === "Neutral") {
      if (this.hasEnabledLayers())
        selector.setEffect(false);
      else
        selector.setEffect(true);
    }
    else if (selector.state === "Disabled")
      selector.setEffect(false);

    if (layer !== null && layer.getVisible() !== selector.effect)
      layer.setVisible(selector.effect);

    return (layer === null && selector.effect);
  }

  updateLayers() {
    let refresh = false;

    for (const layerSelector of this.layerSelectors)
      refresh = refresh || this.updateLayer(layerSelector.layer, layerSelector.selector);

    return refresh;
  }

  getExtent(bounds) {
    if (bounds) {
      let result = ol.extent.boundingExtent([[bounds[0], bounds[1]], [bounds[2], bounds[3]]]);

      if (!ol.extent.isEmpty(result))
        result = ol.proj.transformExtent(
          result,
          ol.proj.get("EPSG:4326"),
          ol.proj.get("EPSG:3857")
        );

      return result;
    }
    else
      return ol.extent.createEmpty();
  }

  updateViewPort() {
    const extent = this.getExtent(this.bounds);

    if (!ol.extent.isEmpty(extent)) {
      if (this.zoomLevel === -1)
        this.map.getView().fit(extent, this.map.getSize());
      else {
        this.map.getView().setCenter(ol.extent.getCenter(extent));

        if (this.map.getView().getZoom() !== this.zoomLevel)
          this.map.getView().setZoom(this.zoomLevel);
      }
    }
  }

  get uri() {
    return this.element.dataset.Uri;
  }
}

function WebPageMapComponentStyle(feature, resolution) {
  var styleFunction = new WebPageMapComponentStyleFunction();

  var fill = styleFunction.getFill(feature);
  var stroke = styleFunction.getStroke(feature);

  var styles = new Array();
  var style = styleFunction.createStyle(feature, fill, stroke);

  if (style !== null)
    styles.push(style);

  return styles;
}

function WebPageMapComponentStyleFunction() {
  this.createStyle = function (feature, fill, stroke) {
    var result = null;
    var type = feature.getGeometry().getType();

    if (type === "Point")
      result = this.createPointStyle(feature, fill, stroke);
    else
      result = this.createRegularStyle(fill, stroke);

    return result;
  }

  this.createPointStyle = function (feature, fill, stroke) {
    var size = feature.getProperties().style["stroke-width"];
    var pointstyle = feature.get("point-type");
    var orientation = feature.get("orientation");
    var result = null;

    if (pointstyle === "custom" && isChrome()) {
      result = new ol.style.Style({
        image: new ol.style.Icon({
          crossOrigin: "anonymous",
          color: fill,
          opacity: fill[3],
          src: "/Resource/" + feature.get("point-source"),
          imgSize: [size, size],
          rotation: orientation * Math.PI / 180,
        })
      });
    }
    else if (pointstyle !== "custom") {
      result = new ol.style.Style({
        image: new ol.style.Circle({
          radius: size,
          fill: new ol.style.Fill({
            color: fill
          }),
          stroke: new ol.style.Stroke({
            color: stroke.getColor(),
            lineDash: stroke.getLineDash()
          })
        })
      });
    }

    return result;
  }

  this.createRegularStyle = function (fill, stroke) {
    var result = null;

    if (fill != null && stroke != null) {
      result = new ol.style.Style({
        fill: new ol.style.Fill({
          color: fill
        }),
        stroke: stroke
      });
    }
    else if (fill != null) {
      result = new ol.style.Style({
        fill: new ol.style.Fill({
          color: fill
        })
      });
    }
    else if (stroke != null) {
      result = new ol.style.Style({
        stroke: stroke
      });
    }

    return result;
  }

  this.getFill = function (feature) {
    var style = feature.getProperties().style;
    var fill = ol.color.asArray(style.fill);

    if (style.opacity != null) {
      if (fill != null) {
        fill = fill.slice();
        fill[3] = style.opacity;
      }
    }

    return fill;
  }

  this.getStroke = function (feature) {
    var style = feature.getProperties().style;
    var stroke = ol.color.asArray(style.stroke);

    if (style.opacity != null) {
      if (stroke != null) {
        stroke = stroke.slice();
        stroke[3] = style.opacity;
      }
    }

    var strokeWidth = style["stroke-width"];
    var strokePattern = style["stroke-pattern"];

    return new ol.style.Stroke({
      color: stroke,
      width: strokeWidth,
      lineDash: strokePattern
    });
  }
}

function WebPageMapComponentHoverStyle(feature, resolution) {
  var styleFunction = new WebPageMapComponentStyleFunction();
  var strokeWidth = feature.getProperties().style["stroke-width"];

  var fill = styleFunction.getFill(feature);
  var stroke = new ol.style.Stroke({
    width: strokeWidth,
    color: "#1677b8"
  });

  var styles = new Array();
  var style = styleFunction.createStyle(feature, fill, stroke);

  if (style !== null)
    styles.push(style);

  return styles;
}

function WebPageMapComponentSelectStyle(feature, resolution) {
  var styleFunction = new WebPageMapComponentStyleFunction();
  var styles = new Array();

  var strokeWidth = feature.getProperties().style["stroke-width"];

  var fill = styleFunction.getFill(feature);
  var stroke = new ol.style.Stroke({
    width: strokeWidth + 2,
    color: "#FFFFFF"
  });

  var style = styleFunction.createStyle(feature, fill, stroke);

  if (style !== null)
    styles.push(style);

  var fill = [22, 119, 184, 0.25];
  var stroke = new ol.style.Stroke({
    width: strokeWidth,
    color: "#1677b8"
  });

  var style = styleFunction.createStyle(feature, fill, stroke);

  if (style !== null)
    styles.push(style);

  return styles;
}

interactivityRegistration.register("Map", function (element) { return new MapComponent(element); });
